import React, { ForwardedRef, forwardRef, useEffect, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import {
  Button,
  ChevronDown,
  Dropdown,
  DropdownContent,
  DropdownGroup,
  DropdownItem,
  DropdownTrigger,
  Icon,
  KebabMenu,
} from '@mesh.ai/rabbit';
import MediaView from 'components/feed/MediaView';
import { PostText } from 'components/feed/PostFeedItem';
import { formatDistanceToNow } from 'date-fns';

import NoGraphData from 'assets/assetsV1/illustrations/newIllustrations/noDataGraph.svg';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Divider } from 'uiToolkitV1/Divider';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useCompetencyDrawerState } from '../competencyDrawerSlice';
import { EditComment } from '../components/EditComment';
import { ProgressChart } from '../components/ProgressChart';
import { SectionContainer } from '../components/SectionContainer';
import { useGetCommentsMutation, useGetLevelUpDetailsQuery } from '../dataHooks';
import { FeedbackType } from '../types/postsRaw';

const limit = 3;

const PostComment = ({ comment }: { comment: FeedbackType }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  if (isEditing) {
    return <EditComment post={comment} setIsEditing={setIsEditing} />;
  }

  if (comment.post.postSubType === 'AUTOMATED_COMMENT') {
    return (
      <Flex p={2} gap={1} alignItems="center" justifyContent="space-between">
        <TextLabel>
          <span style={{ fontWeight: 600, marginRight: '4px' }}>{comment.post.from.metadata.displayName}</span>
          <span>{comment.post.text}</span>
        </TextLabel>
        <TextLabel color="text.g2" minWidth="max-content">
          {formatDistanceToNow(new Date(comment.post.createdTS), { addSuffix: true })}
        </TextLabel>
      </Flex>
    );
  }

  return (
    <Flex gap={2} borderRadius="4px" p={2} bgColor="#F5F7F9" alignItems="flex-start">
      <Avatar size={32} src={comment.post.from.profile.profilePhoto} name={comment.post.from.metadata.displayName} />

      <Flex flexDir="column" w="100%">
        <Flex alignItems="center" justifyContent="space-between" h="36px">
          <TextLabel bold>{comment.post.from.metadata.displayName}</TextLabel>
          <TextLabel color="text.g2">
            {formatDistanceToNow(new Date(comment.post.createdTS), { addSuffix: true })}
          </TextLabel>
        </Flex>
        <PostText text={comment.post.text} macrosRes={comment.post.macros} className="" onPostClick={() => {}} />
        <MediaView media={comment.post.media} />
      </Flex>
      {comment.actions?.edit?.canPerform && (
        <Dropdown>
          <DropdownTrigger>
            <Button size="icon" variant="text">
              <Icon IconSVG={KebabMenu} />
            </Button>
          </DropdownTrigger>
          <DropdownContent>
            <DropdownGroup>
              <DropdownItem onClick={() => setIsEditing(true)}>
                <span>Edit</span>
              </DropdownItem>
            </DropdownGroup>
          </DropdownContent>
        </Dropdown>
      )}
    </Flex>
  );
};

export const History = forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
  const { isLoading, mutate: fetchComments } = useGetCommentsMutation();
  const { comments, commentsOffset, isAllCommentsFetched } = useCompetencyDrawerState();

  const { data: levelUpCompetencyDetails, isLoading: isDetailsLoading } = useGetLevelUpDetailsQuery();

  const fetchMoreComments = () => {
    fetchComments({
      limit,
      offset: commentsOffset,
    });
  };

  useEffect(() => {
    if (comments.length === 0) {
      fetchComments({
        limit,
        offset: commentsOffset,
      });
    }
  }, [comments.length, commentsOffset, fetchComments]);

  return (
    <SectionContainer ref={ref}>
      <TextBodyMedium bold mt={2} mb={4} mx={3}>
        History
      </TextBodyMedium>
      <Divider borderColor="#CFD7DF" />
      {!isDetailsLoading && !levelUpCompetencyDetails?.activity?.length && (
        <EmptyScreen
          heading="No data to show. Checkin your progress"
          description="Nothing to see here yet. Request feedback for better performance enablement!"
          illustrationSrc={NoGraphData}
        />
      )}
      <Flex flexDir="column" gap={3} w="100%">
        <ProgressChart />
        {!!comments?.length && comments.map((comment) => <PostComment key={comment.post.id} comment={comment} />)}
        {isLoading && <SkeletonLoader />}
        {!isAllCommentsFetched && (
          <Button variant="text" onClick={fetchMoreComments} iconRight={ChevronDown}>
            See more
          </Button>
        )}
      </Flex>
    </SectionContainer>
  );
});
